import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { CloseIcon } from '@/components/icons'
import { cn } from '@/lib/cn'
import { SnackbarViewport } from './SnackbarViewport'
import { SuccessSnackbar } from './SuccessSnackbar'
import { useToastPortalTarget } from '@/lib/use-toast-portal-target'

export type ToastVariant = 'success' | 'info' | 'error'

const VARIANT: Record<ToastVariant, { icon: string; bar: string }> = {
  success: { icon: 'bg-emerald-500', bar: 'bg-emerald-400' },
  info: { icon: 'bg-orange-500', bar: 'bg-orange-400' },
  error: { icon: 'bg-red-500', bar: 'bg-red-500' },
}

export interface AppToastProps {
  open: boolean
  title: string
  description: string
  variant?: ToastVariant
  durationMs?: number
  className?: string
  /** Minimal snackbar presentation, without countdown controls. */
  compact?: boolean
  onClose: () => void
}

export function AppToast(props: AppToastProps) {
  if (!props.open) return null
  return <AppToastLive key={props.durationMs} {...props} />
}

function AppToastLive({
  title,
  description,
  variant = 'success',
  durationMs = 4000,
  className,
  compact = false,
  onClose,
}: AppToastProps) {
  const [held, setHeld] = useState(false)
  const [remainingMs, setRemainingMs] = useState(durationMs)
  const heldRef = useRef(false)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    heldRef.current = held
  }, [held])

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    let remaining = durationMs
    let last = performance.now()
    let frame = 0

    const tick = () => {
      const now = performance.now()
      if (!heldRef.current) {
        remaining -= now - last
        setRemainingMs(Math.max(0, remaining))
        if (remaining <= 0) {
          onCloseRef.current()
          return
        }
      }
      last = now
      frame = window.setTimeout(tick, 100)
    }

    frame = window.setTimeout(tick, 100)
    return () => window.clearTimeout(frame)
  }, [durationMs])

  const seconds = Math.max(1, Math.ceil(remainingMs / 1000))
  const colors = VARIANT[variant]
  const portalTarget = useToastPortalTarget()

  if (!portalTarget) return null

  if (variant === 'success')
    return createPortal(
      <SnackbarViewport placement="top-right" className={className}>
        <SuccessSnackbar message={title} description={description} onDismiss={onClose} />
      </SnackbarViewport>,
      portalTarget,
    )

  return createPortal(
    <div
      className={cn('pointer-events-none fixed z-[1100] w-[min(calc(100%-2rem),22rem)]', className)}
      style={{
        top: 'max(1rem, env(safe-area-inset-top))',
        right: 'max(1rem, env(safe-area-inset-right))',
      }}
    >
      <div
        role="alert"
        onClick={compact ? undefined : () => setHeld((current) => !current)}
        className={cn(
          'pointer-events-auto cursor-pointer overflow-hidden rounded-xl bg-[#2b3034] shadow-[0_12px_32px_-8px_rgb(0_0_0_/_0.45)]',
          compact && 'cursor-default bg-[#202020]',
        )}
      >
        <div
          className={cn(
            'flex items-start gap-3 px-3.5 pt-3.5 pb-3',
            compact && 'items-center px-5 py-5',
          )}
        >
          <span
            className={cn(
              'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full',
              colors.icon,
            )}
          >
            <span className="text-sm leading-none font-bold text-white">
              {variant === 'info' ? 'i' : '!'}
            </span>
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white">{title}</p>
            {description ? (
              <p className="mt-0.5 text-xs leading-snug text-slate-300">{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Tutup"
            onClick={(event) => {
              event.stopPropagation()
              onClose()
            }}
            className="-m-3 grid size-11 shrink-0 place-items-center text-slate-300 hover:text-white focus-visible:outline-2 focus-visible:outline-white"
          >
            <CloseIcon className="size-4" />
          </button>
        </div>

        {!compact ? (
          <>
            <div className="border-t border-white/10 px-3.5 py-2">
              <p className="text-[11px] text-slate-300">
                Pesan ini nutup dalam {seconds} detik.{' '}
                <span className="font-bold text-white">
                  {held ? 'Ditahan. Klik buat lanjut.' : 'Klik buat nahan.'}
                </span>
              </p>
            </div>

            <div className="h-1 w-full bg-white/10">
              <div
                className={cn(
                  'h-full origin-left transition-transform duration-100 ease-linear',
                  colors.bar,
                )}
                style={{ transform: `scaleX(${remainingMs / durationMs})` }}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>,
    portalTarget,
  )
}
